import { Component, OnInit, Renderer2, ViewChild } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { ActivatedRoute } from '@angular/router';
import { Modules, NotificationTextMessage } from '@app/core/Enum';
import { VatScheme } from '@app/core/Enum/vat-scheme';
import { CompanyPreference } from '@app/core/Models';
import { CommonService } from '@app/core/Services';
import {
  CreateCompanyPreference,
  GetCompanyVatSchemaId,
  GetCompanyPreferenceData,
  GetCompanyRoundingPreference,
  MenuState,
  UpdateCompanyRoundingPreference,
} from '@app/core/Store';
import { AddCompanyPreferenceComponent } from '@app/modules';
import { AddClosePopupComponent } from '@app/modules/common';
import { Select, Store } from '@ngxs/store';
import { Guid } from 'guid-typescript';
import { NgxSpinnerService } from 'ngx-spinner';
import { Observable, Subject } from 'rxjs';

@Component({
  selector: 'app-company-preference',
  templateUrl: './company-preference.component.html',
  styleUrls: ['./company-preference.component.scss'],
})
export class CompanyPreferenceComponent implements OnInit {
  companyPreferenceData: CompanyPreference;

  companyPreferenceId = Guid.EMPTY as unknown as Guid;
  selectedTab = 0;
  accountProductionTab = 0;
  companyVatSchemaId = 0;
  isRoundingFiguresTabDisabled = false;
  moduleEnum = Modules;
  moduleId = Modules.Invoices;

  @ViewChild(AddCompanyPreferenceComponent)
  companyPreferenceDetail;

  triggerEditData: Subject<any> = new Subject<any>();

  @Select(MenuState.getSelectedMenuPermission)
  isViewPermission$: Observable<any>;
  commonNotificationText = NotificationTextMessage;

  constructor(
    private store: Store,
    public commonService: CommonService,
    private spinner: NgxSpinnerService,
    public dialog: MatDialog,
    private renderer: Renderer2,
    private route: ActivatedRoute
  ) {}

  ngOnInit(): void {
    this.applyTabQueryParams();
    this.getCompanyVatSchemaId();
    this.editCompanyPreferences();
     const saved = localStorage.getItem('alwaysShowActionIcon');
    this.isChecked = saved === 'true';
  
    if (this.isChecked) {
      document.body.classList.add('show-icon');
    }
  }

  applyTabQueryParams(): void {
    const tab = this.route.snapshot.queryParamMap.get('tab');
    const subTab = this.route.snapshot.queryParamMap.get('subTab');

    if (tab === 'account-production') {
      this.selectedTab = 4;
      this.accountProductionTab = subTab === 'rounding-figures' ? 1 : 0;
      return;
    }

    if (tab === 'invoice') {
      this.selectedTab = 0;
    }
  }

  getCompanyVatSchemaId(): void {
    this.store.dispatch(new GetCompanyVatSchemaId()).subscribe((res) => {
      this.companyVatSchemaId = +res.company.companyVatSchemaId;
      this.isRoundingFiguresTabDisabled =
        this.companyVatSchemaId === VatScheme.CashBased;

      if (this.isRoundingFiguresTabDisabled && this.accountProductionTab === 1) {
        this.accountProductionTab = 0;
      }
    });
  }

  editCompanyPreferences(): void {
    let moduleId = this.selectedTabModuleId();
    if (this.moduleId !== Modules.AccountProduction) {
      this.store
        .dispatch(new GetCompanyPreferenceData(moduleId))
        .subscribe((res) => {
          this.companyPreferenceDetail.companyPreferenceForm.markAsUntouched();
          this.companyPreferenceId = res.company.getCompanyPreferenceData.id;
          this.triggerEditData.next(res.company.getCompanyPreferenceData);
        });
    } else {
      this.store
        .dispatch(new GetCompanyRoundingPreference())
        .subscribe((res) => {
          this.companyPreferenceDetail.accountProductionPreferenceForm.markAsUntouched();
          this.triggerEditData.next(res.company.companyRoundingPreference);
        });
    }
  }

  dataSubmit(): boolean {
    this.spinner.show();
    try {
      if (this.moduleId !== Modules.AccountProduction) {
        this.companyPreferenceData = {
          id: this.companyPreferenceId,
          moduleId: this.selectedTabModuleId(),
          autoGenerate:
            this.companyPreferenceDetail.companyPreferenceForm.value
              .autoGenerate,
          prefix:
            this.companyPreferenceDetail.companyPreferenceForm.value.prefix || null,
          suffix:
            this.companyPreferenceDetail.companyPreferenceForm.value.suffix || null,
          repeatType:
            +this.companyPreferenceDetail.companyPreferenceForm.value
              .repeatType,
        };
      }
    } catch (error) {
      this.spinner.hide();
      this.commonService.onFailure(NotificationTextMessage.errorMessage);
      return false;
    }
    return true;
  }

  onSave(isExit: boolean): void {
    if (this.moduleId !== Modules.AccountProduction) {
      if (this.companyPreferenceDetail.companyPreferenceForm.invalid && this.moduleId !== Modules.LogoOnReports) {
        this.commonService.addValidation(
          this.companyPreferenceDetail.companyPreferenceForm,
          this.renderer
        );
      } else {
        if (this.dataSubmit()) {
          this.store
            .dispatch(new CreateCompanyPreference(this.companyPreferenceData))
            .subscribe(
              (res) => {
                if (res !== undefined) {
                  this.onCancel();
                  if (isExit) {
                    history.back();
                  }
                  this.commonService.onSucess(
                    NotificationTextMessage.successMessage
                  );
                } else {
                  this.commonService.onFailure(
                    NotificationTextMessage.errorMessage
                  );
                }
              },
              (err) => {
                this.commonService.onFailure(err.error.Message);
              }
            );
        }
      }
    } else {
      this.store
        .dispatch(
          new UpdateCompanyRoundingPreference(
            this.companyPreferenceDetail.accountProductionPreferenceForm.value.roundUpTo
          )
        )
        .subscribe(
          (res) => {
            if (res !== undefined) {
              this.onCancel();
              if (isExit) {
                history.back();
              }
              this.commonService.onSucess(
                NotificationTextMessage.successMessage
              );
            } else {
              this.commonService.onFailure(
                NotificationTextMessage.errorMessage
              );
            }
          },
          (err) => {
            this.commonService.onFailure(err.error.Message);
          }
        );
    }
  }

  onTabClick(event: any): void {
    this.selectedTab = event;
    if (this.selectedTab !== 4) {
      this.accountProductionTab = 0;
    }
    this.editCompanyPreferences();
  }

  onAccountProductionTabChange(index: number): void {
    if (index === 1 && this.isRoundingFiguresTabDisabled) {
      this.accountProductionTab = 0;
      return;
    }

    this.accountProductionTab = index;
  }

  onCancel(): void {
    this.commonService.isInitialValueChange = false;
    this.companyPreferenceId = Guid.EMPTY as unknown as Guid;
    this.ngOnInit();
  }

  onCloseClick(): void {
    this.dialog.open(AddClosePopupComponent, {});
  }

  selectedTabModuleId(): number {
    let selectedModuleId;

    switch (this.selectedTab) {
      case 0:
        selectedModuleId = Modules.Invoices;
        this.moduleId = Modules.Invoices;
        break;

      case 1:
        selectedModuleId = Modules.Quotation;
        this.moduleId = Modules.Quotation;
        break;

      case 2:
        selectedModuleId = Modules.RecurringInvoice;
        this.moduleId = Modules.RecurringInvoice;
        break;

      case 3:
        selectedModuleId = Modules.RecurringBill;
        this.moduleId = Modules.RecurringBill;
        break;

      case 4:
        selectedModuleId = Modules.AccountProduction;
        this.moduleId = Modules.AccountProduction;
        break;

      case 5:
        selectedModuleId = Modules.LogoOnReports;
        this.moduleId = Modules.LogoOnReports;
        break;
    }

    return selectedModuleId;
  }
    isChecked = false;
  toggleBodyClass(event: any): void {
    this.isChecked = event.checked;
    localStorage.setItem('alwaysShowActionIcon', String(this.isChecked));
  
    if (this.isChecked) {
      document.body.classList.add('show-icon');
    } else {
      document.body.classList.remove('show-icon');
    }
  }
}
